import React from "react";
import { BookOpen, MessageCircle, Sparkles, HeartHandshake, Lightbulb, Activity } from "lucide-react";

const Section = ({ title, children, eyebrow, icon: Icon }) => (
  <section className="mt-12 scroll-mt-24" data-testid={`section-${(eyebrow || title).toLowerCase().replace(/\s+/g, "-")}`}>
    <div className="flex items-center gap-3 mb-5">
      {Icon && (
        <div className="w-10 h-10 rounded-xl bg-[#E6F0E9] text-[#2C4C3B] flex items-center justify-center">
          <Icon className="w-5 h-5" />
        </div>
      )}
      <div>
        {eyebrow && <span className="text-xs tracking-[0.2em] uppercase text-[#C66A51]">{eyebrow}</span>}
        <h2 className="font-display text-2xl sm:text-3xl text-[#2C4C3B]">{title}</h2>
      </div>
    </div>
    <div className="space-y-4 text-[#52606D] leading-relaxed">{children}</div>
  </section>
);

const Card = ({ title, body, accent }) => (
  <div className={`p-5 rounded-2xl border ${accent ? "bg-[#E6F0E9] border-[#2C4C3B]/15" : "bg-white border-[#E2E8F0]"}`}>
    <div className="font-display text-base text-[#2C4C3B]">{title}</div>
    <div className="text-sm mt-2 text-[#52606D]">{body}</div>
  </div>
);

export default function ClinicalNotes() {
  return (
    <div className="max-w-5xl mx-auto px-6 py-14" data-testid="clinical-notes-page">
      <span className="text-xs tracking-[0.2em] uppercase text-[#C66A51]">Reference</span>
      <h1 className="font-display text-4xl sm:text-5xl font-semibold text-[#2C4C3B] mt-2">Clinical notes for AAC programming</h1>
      <p className="text-[#52606D] mt-3 max-w-2xl">A condensed reference of modelling principles, partner skills, ratio guidance, and communicative-function vocabulary — drawn from our Founder's clinical practice.</p>

      {/* Ratio table */}
      <Section title="Core / Fringe ratio by communicator stage" eyebrow="Ratio guidance" icon={Sparkles}>
        <div className="overflow-x-auto rounded-2xl border border-[#E2E8F0] bg-white">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-[#F4F5F0] text-left">
                <th className="px-4 py-3 text-xs uppercase tracking-[0.1em] text-[#52606D]">Stage</th>
                <th className="px-4 py-3 text-xs uppercase tracking-[0.1em] text-[#52606D]">Core</th>
                <th className="px-4 py-3 text-xs uppercase tracking-[0.1em] text-[#52606D]">Fringe</th>
              </tr>
            </thead>
            <tbody>
              {[
                ["Emergent", "80%", "20%"],
                ["Wants & Needs", "70%", "30%"],
                ["Context-Cue", "60%", "40%"],
                ["Transitional", "50%", "50%"],
                ["Independent", "50%", "50%"],
              ].map(([s, c, f], i) => (
                <tr key={i} className="border-t border-[#E2E8F0]">
                  <td className="px-4 py-3 font-medium text-[#2C4C3B]">{s}</td>
                  <td className="px-4 py-3"><span className="px-2.5 py-1 rounded-full text-xs core-badge">{c}</span></td>
                  <td className="px-4 py-3"><span className="px-2.5 py-1 rounded-full text-xs fringe-badge">{f}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Section>

      {/* Modelling principles */}
      <Section title="Modelling principles" eyebrow="Aided language stimulation" icon={Lightbulb}>
        <div className="grid sm:grid-cols-2 gap-4">
          <Card title="Model one step above" body="Use AAC at a level slightly higher than the learner's current expressive output." />
          <Card title="Speak + tap together" body="Tap the symbol on the device while speaking the complete sentence aloud." />
          <Card title="Skip strict grammar early" body="At early stages, do NOT model grammatically perfect sentences — keep it functional." />
          <Card title="Statements, not questions" body="Foster connection, motivation and curiosity. Comment more than you quiz." />
          <Card title="Self-talk" body="Narrate what YOU are doing: 'I'm washing the dishes. Now I'm drying them. All done.'" />
          <Card title="Parallel talk" body="Comment on what the CHILD is doing: 'You're building a big tower!'" />
          <Card title="Don't force imitation" body="Pressure flips the dynamic. Repeat your model warmly and move on." />
          <Card title="Cycle through contexts" body="Re-use target words across activities and communicative functions." />
          <Card title="Descriptive teaching" body="Concept-focused — explain new words using the core words the learner already has." />
          <Card title="Praise attempts" body="'Wow, that was a great try!' — value the communication attempt over precision." accent />
        </div>
      </Section>

      {/* Communication partner skills */}
      <Section title="Communication partner skills" eyebrow="Mindset" icon={HeartHandshake}>
        <ul className="list-disc pl-6 space-y-1 text-[#1A202C]">
          <li>Presume competence — always.</li>
          <li>Be persistent and consistent across people and settings.</li>
          <li>Make interaction interactive, connecting, motivating and fun.</li>
          <li>Be patient — allow processing time after every model.</li>
          <li>Make AAC available 24/7. The device is never taken away.</li>
        </ul>
        <p className="text-sm">AAC must travel with the learner — home, school, therapy, community, shopping, outings. Use straps or harnesses for portability.</p>
      </Section>

      {/* Communicative functions table */}
      <Section title="Communicative functions → core words" eyebrow="Pragmatics" icon={MessageCircle}>
        <div className="overflow-x-auto rounded-2xl border border-[#E2E8F0] bg-white">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-[#F4F5F0] text-left">
                <th className="px-4 py-3 text-xs uppercase tracking-[0.1em] text-[#52606D]">Function</th>
                <th className="px-4 py-3 text-xs uppercase tracking-[0.1em] text-[#52606D]">Core words</th>
              </tr>
            </thead>
            <tbody>
              {[
                ["Request", "want, what that, want more, want another, want full, want help, help me"],
                ["Social conversation", "What? What do you like? I like, Where you go? Do you like?"],
                ["Protest / reject", "stop, stop music, not that, no, no more"],
                ["Direct another / instruct", "get, get it, give, give me, open it, go down, come up"],
                ["Ask questions", "what, what that?, where, when?, who?"],
                ["Describe", "dirty, different, big, small, wet, dry"],
                ["Agree / disagree", "yes, no"],
                ["Express feelings", "happy, sad, excited, upset, frustrated, confused, angry"],
                ["Give opinion", "good, I like, I don't like, bad, that bad"],
                ["Share something", "I see, I go, I eat, I like, I did"],
                ["Comment", "see, look, no!"],
              ].map(([f, w], i) => (
                <tr key={i} className="border-t border-[#E2E8F0]">
                  <td className="px-4 py-3 font-medium text-[#2C4C3B] whitespace-nowrap">{f}</td>
                  <td className="px-4 py-3 text-[#52606D]">{w}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Section>

      {/* Attribute meaning */}
      <Section title="Attribute meaning to behaviours" eyebrow="Pre-intentional & emerging" icon={Activity}>
        <div className="overflow-x-auto rounded-2xl border border-[#E2E8F0] bg-white">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-[#F4F5F0] text-left">
                <th className="px-4 py-3 text-xs uppercase tracking-[0.1em] text-[#52606D]">Function</th>
                <th className="px-4 py-3 text-xs uppercase tracking-[0.1em] text-[#52606D]">Behaviour</th>
                <th className="px-4 py-3 text-xs uppercase tracking-[0.1em] text-[#52606D]">Vocabulary to model</th>
              </tr>
            </thead>
            <tbody>
              {[
                ["Express requests", "Reaching, pointing, dragging", "want, give, more, help"],
                ["Sharing opinion", "Look of disgust, smiling", "like, don't like, good, not good, I think, I feel"],
                ["Reject / protest", "Crying, whining, aggression", "stop, no, don't"],
                ["Direct actions", "Hands you something", "do, get, play, blow, go, push, up, down, open, close, on, off"],
                ["Sharing information", "Showing curiosity", "what, look"],
                ["Greetings & manners", "Smiling on arrival", "hello, how are you, please, thank you, sorry, okay, happy birthday"],
              ].map(([f, b, v], i) => (
                <tr key={i} className="border-t border-[#E2E8F0]">
                  <td className="px-4 py-3 font-medium text-[#2C4C3B] whitespace-nowrap">{f}</td>
                  <td className="px-4 py-3 text-[#52606D]">{b}</td>
                  <td className="px-4 py-3 text-[#52606D]">{v}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Section>

      <Section title="When Avaz is the device" eyebrow="Device-specific" icon={BookOpen}>
        <ul className="list-disc pl-6 space-y-1 text-[#1A202C]">
          <li>Avaz offers easy access to <strong>core words</strong> — anchor vocabulary lessons here.</li>
          <li>Use the <strong>Quick Folder</strong> for pre-stored urgent messages (e.g., help, alert, café orders).</li>
          <li>The <strong>Alert</strong> button gets adult attention in noisy or unsafe situations — model in school group activities.</li>
          <li>Grammar / morphology supports: tenses (past, present, future, continuous), plurals, possessives, comparatives, superlatives, pronouns (you / yourself / your / yours).</li>
          <li>Add <strong>personal fringe vocabulary</strong> (names of family, places, foods) directly in the device for motivation.</li>
        </ul>
      </Section>

      <p className="mt-16 text-xs tracking-[0.2em] uppercase text-[#52606D]">
        Curated by Sadiya Yasin · A product of Sensiiq
      </p>

      {/* ───────────── Terms & Conditions ───────────── */}
      <section id="terms" className="mt-24 pt-16 border-t border-[#E2E8F0] scroll-mt-24" data-testid="terms-section">
        <span className="text-xs tracking-[0.2em] uppercase text-[#C66A51]">Legal</span>
        <h2 className="font-display text-3xl sm:text-4xl text-[#2C4C3B] mt-2">Terms &amp; Conditions</h2>
        <p className="text-xs tracking-[0.2em] uppercase text-[#52606D] mt-2">Effective May 2026</p>

        <div className="mt-10 space-y-8 text-[#52606D] leading-relaxed text-[15px]">
          <div>
            <h3 className="font-display text-lg text-[#2C4C3B] mb-2">1. Acceptance of Terms</h3>
            <p>By creating an account or using CommPass by Sensiiq, you agree to these Terms and Conditions. If you are accessing on behalf of a clinic, school or institution, you confirm you have authority to bind that organisation to these terms.</p>
          </div>

          <div>
            <h3 className="font-display text-lg text-[#2C4C3B] mb-2">2. Platform Use</h3>
            <p>CommPass by Sensiiq is a clinical decision-support tool designed for use by speech-language pathologists, educators, AAC specialists and trained professionals. Activities, word lists and goal banks generated on this platform are intended to support clinical programming and must be reviewed and applied by a qualified professional. Sensiiq does not provide direct therapeutic services or replace clinical judgment.</p>
          </div>

          <div>
            <h3 className="font-display text-lg text-[#2C4C3B] mb-2">3. Subscription and Billing</h3>
            <p>Subscriptions are billed monthly or annually depending on your selected plan. Prices are listed in Indian Rupees (INR). International users may be billed in USD at equivalent rates. Annual plans are billed as a single upfront payment. You may cancel at any time; cancellation takes effect at the end of your current billing period. No refunds are issued for partial billing periods. Institutional plans are managed under one admin email with access for up to three users. NGO and institutional bulk pricing is available on request.</p>
          </div>

          <div>
            <h3 className="font-display text-lg text-[#2C4C3B] mb-2">4. AI-Generated Content</h3>
            <p>The Professional and Institutional plans include AI-powered features. AI-generated activities, goal suggestions and word lists are produced to support clinical programming and are not a substitute for professional evaluation. The accuracy of AI outputs depends on the information entered by the user. Sensiiq is not liable for clinical decisions made on the basis of AI-generated content alone.</p>
          </div>

          <div>
            <h3 className="font-display text-lg text-[#2C4C3B] mb-2">5. Intellectual Property</h3>
            <p>All content, frameworks, activity structures and programming logic on this platform are the intellectual property of Sensiiq. Users may export and use generated content for their own clinical practice. Redistribution, resale or reproduction of platform content without written permission from Sensiiq is prohibited.</p>
          </div>

          <div>
            <h3 className="font-display text-lg text-[#2C4C3B] mb-2">6. Privacy and Data</h3>
            <p>User data is handled in accordance with our Privacy Policy and applicable data protection regulations including the Digital Personal Data Protection Act, India, 2023. Sensiiq does not sell or share user data with third parties. Client information entered for programming purposes should be de-identified in accordance with your professional obligations and regional regulations.</p>
          </div>

          <div>
            <h3 className="font-display text-lg text-[#2C4C3B] mb-2">7. Limitation of Liability</h3>
            <p>Sensiiq provides this platform in good faith for professional clinical use. To the fullest extent permitted by applicable law, Sensiiq is not liable for any clinical outcomes, indirect damages or losses arising from use of the platform or reliance on AI-generated outputs.</p>
          </div>

          <div>
            <h3 className="font-display text-lg text-[#2C4C3B] mb-2">8. Changes to Terms</h3>
            <p>Sensiiq reserves the right to update these terms. Users will be notified of material changes by email. Continued use of the platform after notification constitutes acceptance of the updated terms.</p>
          </div>

          <div>
            <h3 className="font-display text-lg text-[#2C4C3B] mb-2">9. Contact</h3>
            <p>For questions about subscriptions, billing or these terms, contact us at <a href="mailto:sensiiq.services@gmail.com" className="text-[#2C4C3B] font-medium hover:underline">sensiiq.services@gmail.com</a>.</p>
          </div>
        </div>
      </section>
    </div>
  );
}
